import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { createManagementEfficiencyRatio } from '@/modules/Report/components/BusinessPerformance/services'
import { ManagementEfficiencyRatioCreateParams } from '@/modules/Report/components/BusinessPerformance/types'
import { ModalProps } from '@/types/shared'
import { yupResolver } from '@hookform/resolvers/yup'
import { useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { Button, Dialog, FormItem, TextField, Typogaphy } from 'vnetwork-ui'
import * as yup from 'yup'

export interface BatchInputModalProps extends ModalProps {
    refetch?: () => void
}

interface MonthData {
    roa: number
    roe: number
    roic: number
    roce: number
}

interface FormData {
    year: number
    months: {
        [key: string]: MonthData // key format: "2025-01", "2025-02", etc.
    }
    notes?: string
}

const currentYear = dayjs().year()

const BatchInputModal: React.FC<BatchInputModalProps> = ({
    open,
    onClose,
    refetch,
}) => {
    const { t } = useTranslation()
    const queryClient = useQueryClient()
    const [isSubmitting, setIsSubmitting] = useState(false)

    // Form schema
    const schema = useMemo(
        () =>
            yup.object().shape({
                year: yup.number().required(t('required')),
                notes: yup.string(),
            }),
        [t]
    )

    // Generate default month data
    const generateDefaultMonths = (year: number) => {
        const months: FormData['months'] = {}
        for (let i = 1; i <= 12; i++) {
            const monthKey = `${year}-${i.toString().padStart(2, '0')}`
            months[monthKey] = {
                roa: 0,
                roe: 0,
                roic: 0,
                roce: 0,
            }
        }
        return months
    }

    // Default values
    const defaultValues: FormData = useMemo(
        () => ({
            year: currentYear,
            months: generateDefaultMonths(currentYear),
            notes: '',
        }),
        []
    )

    const formControl = useForm<FormData>({
        defaultValues,
        // @ts-ignore
        resolver: yupResolver(schema),
    })

    const { control, handleSubmit, reset, watch, setValue } = formControl
    const selectedYear = watch('year')
    const months = watch('months')

    // Reset months when year changes
    useEffect(() => {
        if (selectedYear) {
            setValue('months', generateDefaultMonths(selectedYear))
        }
    }, [selectedYear, setValue])

    // Reset form when dialog opens/closes
    useEffect(() => {
        if (open) {
            reset({
                year: currentYear,
                months: generateDefaultMonths(currentYear),
                notes: '',
            })
        }
    }, [open, reset])

    const onSubmit = async (values: FormData) => {
        try {
            setIsSubmitting(true)
            const promises: Promise<any>[] = []

            // Loop through all months and create ratios for non-zero values
            Object.entries(values.months).forEach(([monthKey, data]) => {
                // Only create if at least one value is non-zero
                if (
                    data.roa > 0 ||
                    data.roe > 0 ||
                    data.roic > 0 ||
                    data.roce > 0
                ) {
                    const payload: ManagementEfficiencyRatioCreateParams = {
                        report_month: monthKey,
                        roa: data.roa,
                        roe: data.roe,
                        roic: data.roic,
                        roce: data.roce,
                        notes: values.notes || '',
                    }
                    promises.push(createManagementEfficiencyRatio(payload))
                }
            })

            // Check if there's at least one ratio to create
            if (promises.length === 0) {
                toast.error(t('at_least_one_value_required'))
                setIsSubmitting(false)
                return
            }

            // Execute all API calls
            await Promise.all(promises)

            // Invalidate queries
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.REPORTS_BI_MANAGEMENT_EFFICIENCY_RATIOS],
            })

            // Show success message
            toast.success(t('create-success'))

            // Refetch and close
            refetch?.()
            onClose()
        } catch (error) {
            toast.error(t('something went wrong'))
            console.error(
                'Failed to create management efficiency ratios:',
                error
            )
        } finally {
            setIsSubmitting(false)
        }
    }

    // Month names for header
    const monthNames = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec',
    ]

    // Ratio fields
    const ratioFields = [
        { key: 'roa', label: t('roa') },
        { key: 'roe', label: t('roe') },
        { key: 'roic', label: t('roic') },
        { key: 'roce', label: t('roce') },
    ]

    return (
        <Dialog
            open={open}
            onClose={onClose}
            title={t('batch_input_management_efficiency')}
            maxWidth="full"
        >
            <form
                onSubmit={handleSubmit(onSubmit as any)}
                className="space-y-4 p-4"
            >
                <FormItem
                    control={control}
                    name="year"
                    type="select"
                    label={t('year')}
                    options={Array.from({ length: 5 }, (_, index) => ({
                        label: `${currentYear - index}`,
                        value: currentYear - index,
                    }))}
                    allowClear={false}
                />
                {/* Batch Input Table */}
                <div className="space-y-2">
                    <Typogaphy className="text-base font-medium text-primary">
                        {t('monthly_ratios_input')}
                    </Typogaphy>
                    <div className="max-h-[500px] overflow-auto border rounded-md">
                        <table className="w-full border-collapse">
                            <thead className="bg-gray-50 sticky top-0 z-10">
                                <tr>
                                    <th className="px-3 py-3 text-left text-sm font-medium text-gray-700 border-b border-r bg-gray-50 sticky left-0 z-20 min-w-[200px]">
                                        {t('ratio')}
                                    </th>
                                    {monthNames.map((month, idx) => (
                                        <th
                                            key={idx}
                                            className="px-3 py-3 text-center text-sm font-medium text-gray-700 border-b min-w-[100px]"
                                        >
                                            {month} {selectedYear}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {ratioFields.map((field) => (
                                    <tr
                                        key={field.key}
                                        className="hover:bg-gray-50"
                                    >
                                        <td className="px-3 py-2 text-sm font-medium text-gray-900 border-b border-r bg-white sticky left-0 z-10">
                                            {field.label}
                                        </td>
                                        {Array.from(
                                            { length: 12 },
                                            (_, monthIdx) => {
                                                const monthKey = `${selectedYear}-${(monthIdx + 1).toString().padStart(2, '0')}`
                                                return (
                                                    <td
                                                        key={monthIdx}
                                                        className="px-2 py-2 border-b border-r"
                                                    >
                                                        <TextField
                                                            type="number"
                                                            step="0.01"
                                                            min="0"
                                                            size="small"
                                                            className="w-full"
                                                            value={
                                                                months[
                                                                    monthKey
                                                                ]?.[
                                                                    field.key as keyof MonthData
                                                                ] || 0
                                                            }
                                                            onChange={(e) => {
                                                                const value =
                                                                    parseFloat(
                                                                        e.target
                                                                            .value
                                                                    ) || 0
                                                                setValue(
                                                                    `months.${monthKey}.${field.key}` as any,
                                                                    value
                                                                )
                                                            }}
                                                        />
                                                    </td>
                                                )
                                            }
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Notes */}
                <FormItem
                    control={control}
                    name="notes"
                    type="textarea"
                    label={t('notes')}
                    placeholder={t('enter_notes')}
                />

                {/* Actions */}
                <div className="flex justify-end gap-2">
                    <Button
                        type="button"
                        variant="outlined"
                        onClick={onClose}
                        disabled={isSubmitting}
                    >
                        {t('cancel')}
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={isSubmitting}
                        loading={isSubmitting}
                    >
                        {t('create_all')}
                    </Button>
                </div>
            </form>
        </Dialog>
    )
}

export default BatchInputModal
